import { Injectable, inject } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { bufferTime, filter } from "rxjs/operators";

import { AuthService } from "../auth/auth.service";
import { HelpersService } from "../helpers-service/helpers.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { IColumnDataLayoutService } from "./../column-data-layout-services/column-data-layout.interface";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { FiltersService } from "../filters-service/filters.service";
import { CardDisplayService } from "../card-display/card-display.service";
import { CardEventType } from "../interfaces/card-event-type.enum";
import { TaxReturnStatusesService } from "../tax-return-statuses-service/tax-return-statuses.service";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";

@Injectable()
export class ColumnDataLayoutService implements IColumnDataLayoutService {
    private readonly authService = inject(AuthService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly taxReturnStatusService = inject(TaxReturnStatusesService);

    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    cardSize: number = 100; // px
    cardSizeGrid = 39; // px
    containerSize: number;
    cardsPerColumn = {};
    currentViewTypeSubscription: Subscription;
    columnLoadedSubscription: Subscription;
    cardsLoadedSubscription: Subscription;
    cardChangedSubscription: Subscription;
    singleCardFetchLimit = 1000;
    loadedColumns = {};
    cardsAreLoaded = false;
    public columnLoadedSubject = new Subject<any>();
    columnLoadedEvent$ = this.columnLoadedSubject.asObservable();
    public loadCardsFromCacheSubject = new Subject<any>();
    loadCardsFromCacheEvent$ = this.loadCardsFromCacheSubject.asObservable();
    dashboardChangeSubscription: Subscription;
    currentHttpRequests: Subscription[] = [];
    indexedDBInitialized = false;
    isJobsBillingDashboard = false;

    constructor() {
        const helpersService = this.helpersService;

        this.currentViewTypeSubscription = this.filtersService.cardViewService.viewTypeChangeEvent$.subscribe((type) => {
            if (type !== "settings") {
                setTimeout(() => {
                    helpersService.columnWrapperCalPreviousTop = 0;
                }, 200);
                if (this.authService.loggedIn()) {
                    this.showUnMappedCardsToast();
                }
            }
        });
        this.columnLoadedSubscription = this.columnLoadedEvent$.subscribe((data) => {
            this.loadedColumns[data.columnId] = data.cards;
        });

        // Bridge SignalR-driven card changes (via CardDisplayService.cardChangedEvent$) onto
        // loadCardsFromCacheSubject so subscribers like card-modal-component can react to
        // concurrent updates. Restored after DASH-20562 removed the original wiring. Events
        // are buffered to mirror the pre-regression behaviour from DASH-12063 and avoid UI
        // thrash under bursts of SignalR notifications.
        this.cardChangedSubscription = this.cardDisplayService.cardChangedEvent$
            .pipe(
                bufferTime(50),
                filter((events) => events.length > 0),
            )
            .subscribe((events) => {
                const loadEvents = events
                    .map((event) => {
                        const card = event.newCard ?? event.oldCard;
                        if (!card?.cardId) {
                            return null;
                        }
                        switch (event.eventType) {
                            case CardEventType.cardUpdated:
                                return { type: "updated", cardId: card.cardId, card };
                            case CardEventType.cardAdded:
                                return { type: "added", cardId: card.cardId, card };
                            case CardEventType.cardDeleted:
                                return { type: "delete", cardId: card.cardId, card };
                            default:
                                return null;
                        }
                    })
                    .filter((e) => e !== null);
                if (loadEvents.length > 0) {
                    this.loadCardsFromCacheSubject.next(loadEvents);
                }
            });

        setTimeout(() => {
            this.helpersService.cardDownloadEmitter.next(true);
        });

        if (this.authService.loggedIn()) {
            if (!this.indexedDBInitialized) {
                this.initIndexedDB();
            }
        }
        this.cardsLoadedSubscription = this.loadCardsFromCacheEvent$.subscribe((data) => {
            if (!data || data.type === "batch") {
                // initial load
                this.cardsAreLoaded = true;
            }
        });

        this.dashboardChangeSubscription = this.dashboardsService.dashboardChangeEvent$.subscribe(() => {
            this.indexedDBInitialized = false;
            this.cardsPerColumn = {};
            this.loadedColumns = {};
            this.currentHttpRequests.forEach((s) => s.unsubscribe());
            this.currentHttpRequests = [];
            this.cardsAreLoaded = false;
            this.responseErrorHandlerService.hidePopup("hiddenCards");
            this.responseErrorHandlerService.hidePopup("hiddenJobsCards");
        });

        this.dashboardsService.currentDashboardChanged$.subscribe(() => {
            this.initIndexedDB();
        });
    }

    async initIndexedDB() {
        const dashboard = await this.dashboardsService.getCurrentDashboard();
        this.isJobsBillingDashboard = dashboard ? isJobsBilling(dashboard.ApplicationType) : false;
        await this.showUnMappedCardsToast();
        this.loadCardsFromCacheSubject.next(false);
        this.helpersService.cardDownloadEmitter.next(false);
    }

    triggerSetColumnWrapperHeight() {
        this.helpersService.columnWrapperCalPreviousTop = 0;
        this.helpersService.setColumnWrapperHeight();
    }

    async showUnMappedCardsToast() {
        const model = await this.cardDisplayService.getModel();

        if (!this.authService.connectedUserCanManageSettings()) {
            return false;
        }
        if (this.filtersService.cardViewService.currentViewType === "all") {
            return false;
        }

        const numberOfUnassigned = model.filter((it) => it.state === "unmapped").reduce((acc, status) => acc + this.countCards(status.cards), 0);

        // add Deleted statuses
        const statuses = await this.taxReturnStatusService.getTaxReturnStatusesList();
        const numberOfDeleted = model
            .filter((modelEntry) => !statuses?.find((it) => it.Id === modelEntry.statusId))
            .reduce((acc, modelEntry) => acc + this.countCards(modelEntry.cards), 0);

        const numberOfTaxReturns = numberOfUnassigned + numberOfDeleted;

        if (numberOfTaxReturns > 0) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ALERT",
                messageCode: this.isJobsBillingDashboard ? "hiddenJobsCards" : "hiddenCards",
                additionInfo: numberOfTaxReturns,
            });
        }
    }

    private countCards(cards: any[]): number {
        return this.isJobsBillingDashboard
            ? cards.length
            : cards.reduce(
                  (acc, card) => acc + (this.filtersService.deleteCardByGrouping(card) ? 0 : this.helpersService.getClientFileCount(card)),
                  0,
              );
    }
}
